/**
 * App Open Interval Trigger Component
 *
 * Trigger: "opened the app (interval)"
 * Shows interval number + unit bound to delayInterval/delayType.
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { TriggerComponentProps } from '../automationTriggers';

const INTERVAL_DELAY_TYPES = [
  { value: 'minute', label: 'minute(s)' },
  { value: 'hour', label: 'hour(s)' },
  { value: 'day', label: 'day(s)' },
  { value: 'week', label: 'week(s)' },
  { value: 'month', label: 'month(s)' },
];

export const AppOpenIntervalTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const delayInterval = automation.delayInterval ?? 0;
  const delayType = automation.delayType || 'day';

  if (readOnly) {
    if (delayInterval === 0) {
      return <span>{t('automations.triggers.openedAppInterval')}</span>;
    }
    const unitLabel = t(`automations.timing.delayUnit.${delayType}_plural`);
    return (
      <span>
        {t('automations.triggers.openedAppIntervalAfter', {
          count: delayInterval,
          unit: unitLabel,
        })}
      </span>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <FormTextField
        name={`automations.${index}.delayInterval`}
        type="number"
        sx={{ width: 80 }}
        InputProps={{ inputProps: { min: 0 } }}
      />
      <Box sx={{ minWidth: 120 }}>
        <FormSelect
          name={`automations.${index}.delayType`}
          options={INTERVAL_DELAY_TYPES}
        />
      </Box>
    </Stack>
  );
};
